/**
 * 学生档案接口（家长视角）。
 *
 * ⚠️ 学生端的"今日打卡"已迁到 `lib/api/checkins.ts`（那边有完整的计时字段与
 * "接口未就绪"三态），这里不再重复实现。积分只保留**只读**的余额与流水，
 * 供家长端孩子详情页使用；完整的积分/调分/冲正在 `lib/api/points.ts`。
 */
import {
  SHARE_STATUSES,
  gradeToUiMode,
  type ShareStatus,
  type UiMode,
} from '@fht/shared';
import { api, tryRequest, withFallback } from './client';
import { pickArray, pickObject, toOptionalNumber } from './shape';
import type {
  CreateStudentResponse,
  PointsSummary,
  StudentListItem,
  StudentShare,
  StudentShareLists,
  StudentSummary,
} from './types';

export interface CreateStudentPayload {
  nickname: string;
  username: string;
  gender: 'male' | 'female';
  birthday: string;
  gradeCode: string;
  /** 不传则由服务端生成强密码并一次性返回。 */
  initialPassword?: string;
}

export interface UpdateStudentPayload {
  nickname?: string;
  gender?: 'male' | 'female';
  birthday?: string;
  gradeCode?: string;
  uiModeOverride?: UiMode | null;
}

/**
 * 列表项的原始记录：除余额外都与 `StudentListItem` 一致。
 *
 * 之所以把余额单独写成 `unknown`，是因为它必须**显式解析**而不能直接断言成
 * `number` —— 见下面 `toStudentListItem` 的说明。
 * `shares.ts` 的"共享给我的"走同一个规整函数，因此也复用这个类型。
 */
export type RawStudentListItem = Omit<StudentListItem, 'pointsBalance'> & {
  pointsBalance?: unknown;
};

/**
 * 把 `GET /students` 的一条记录规整成列表项。
 *
 * ⚠️ 这里**只**做一件事：把余额显式解析成 `number | null`。
 *   这条规则守的是「孩子卡片显示 0 积分」那个线上 bug：
 *   后端当时根本不返回 `pointsBalance`，而卡片写的是 `?? 0` ——
 *   "没读到"被冒充成"0 分"。改成显式解析后：
 *     - 后端给了真实数字 → 原样显示（包含合法的 0）；
 *     - 后端没给 / 给了非法值 → `null`，界面显示「—」，让问题**看得见**。
 *   其余字段保持原样透传，避免在这里引入不必要的形状转换。
 */
export function toStudentListItem(raw: RawStudentListItem): StudentListItem {
  return { ...raw, pointsBalance: toOptionalNumber(raw.pointsBalance) };
}

/**
 * 我可见的学生列表。
 *
 * ⚠️ 余额来自后端 `GET /students` 的 `pointsBalance`（`LEFT JOIN points_account`）。
 *   前端以前是**逐个孩子再发一次** `/students/:id/points` 才能拿到余额，
 *   而首页卡片干脆就没发 —— 直接读了一个不存在的字段并兜底成 0。
 */
export async function listStudents(): Promise<StudentListItem[]> {
  return withFallback(async () => {
    const data = await api.get<{ students: RawStudentListItem[] }>('/students');
    return (data.students ?? []).map(toStudentListItem);
  }, [] as StudentListItem[]);
}

/**
 * `GET /students/:id` 的响应形状：**档案在 `student` 里，权限在外层**。
 * 这是两个不同层级的键，不是"student 里也带 permission"。
 */
interface StudentDetailPayload {
  student: StudentSummary;
  permission: StudentSummary['permission'];
  isOwner: boolean;
}

/**
 * 取单个学生的档案。
 *
 * ⚠️ 这里曾经有一个**TypeScript 查不出来的**类型谎言，影响全站 8 个页面
 *   （目标、打卡任务、奖品、统计、积分明细、兑换审批、孩子详情、学生端预览）。
 *
 *   早期实现是 `api.get<StudentSummary>(...)` —— 那只是**断言**响应形状，
 *   运行期不做任何校验。而后端返回的是 `{ student, permission, isOwner }`，
 *   于是调用方在同一层读字段时：
 *     - `permission` **读得到**（它本来就在外层）→ 权限控制看起来完全正常
 *     - `nickname` / `gradeCode` / `birthday` / `uiModeOverride` **全是 undefined**
 *       → 页面标题少了孩子的名字；年级回落到 `GRADES[0]`（显示成"小学一年级"）
 *   表象极具误导性：没有报错、权限也对，只是"很多信息没有"。
 *
 *   修法：显式拍平成 `StudentSummary`（它本身就把 `permission` 定义为同层字段），
 *   这样 8 个调用点无需改动，且返回值与它们的假设一致。
 */
export async function getStudent(id: string): Promise<StudentSummary | null> {
  return withFallback(
    async () => {
      const data = await api.get<StudentDetailPayload>(`/students/${id}`);
      return { ...data.student, permission: data.permission };
    },
    null as StudentSummary | null,
  );
}

export async function createStudent(payload: CreateStudentPayload): Promise<CreateStudentResponse> {
  return api.post<CreateStudentResponse>('/students', payload);
}

/**
 * 更新档案的响应：`{ student, changed }`。
 *
 * ⚠️ 此处的 `student` 是**刚更新的那条档案记录**，**不含 `permission`**
 *   （权限是"当前登录家长对该学生的关系"，不是档案字段）。
 *   原先断言成 `StudentSummary` 属于与 `getStudent` 同类的类型谎言 ——
 *   这里如实声明，避免调用方误以为能拿到 `permission`。
 */
export interface UpdateStudentResult {
  student: Omit<StudentSummary, 'permission'>;
  changed: string[];
}

export async function updateStudent(
  id: string,
  payload: UpdateStudentPayload,
): Promise<UpdateStudentResult> {
  return api.patch<UpdateStudentResult>(`/students/${id}`, payload);
}

export async function resetStudentPassword(
  id: string,
  body: { newPassword?: string; forceChangeOnNextLogin?: boolean } = {},
): Promise<{ initialPassword: string | null }> {
  return api.post<{ initialPassword: string | null }>(`/students/${id}/password/reset`, body);
}

export async function deleteStudent(id: string): Promise<void> {
  await api.del(`/students/${id}`);
}

// ---------------------------------------------------------------- 共享

/**
 * 读取一条共享记录的状态。
 *
 * 认不出来的（缺失 / 不是枚举里的字符串）**按 `accepted` 处理**：这是这次改动
 * 之前的旧行为（`status: 'accepted'`），保留它是为了不因为一个畸形字段就
 * 把整条记录藏起来 —— 真正的过滤在下面 `readShareLists()` 里按状态做。
 */
function readShareStatus(raw: unknown): ShareStatus {
  return typeof raw === 'string' && (SHARE_STATUSES as readonly string[]).includes(raw)
    ? (raw as ShareStatus)
    : 'accepted';
}

/**
 * 把 `GET /students/:id/shares` 的一条**原始记录**规整成前端模型。
 *
 * ⚠️ 字段名必须与后端一致（见 `apps/api/src/modules/shares/shares.service.ts`
 *   的 `listStudentShares`）。后端返回的每一条是：
 *
 *      { id, toParentId, toParentNickname, toParentUsername,
 *        permission, status, expiresAt, respondedAt, createdAt }
 *
 *   即"对方"一律叫 **`toParent*`**（这条记录描述的是"把学生共享给谁"）。
 *
 *   这里原来是把原始行**直接断言**成 `StudentShare`（字段是 `parentNickname` /
 *   `parentUsername`），于是孩子详情页「已共享的家长」那一行只剩
 *   「可编辑 · 10-13 20:55」：两个名字字段都是 `undefined`，
 *   React 把它们渲染成空字符串 —— **不报错、不白屏、也不显示人**。
 *   用户看到的就是"接受共享的人没有显示"。
 *
 *   现在显式读 `toParent*`（旧名 `parent*` 保留兼容），昵称缺失时由调用方
 *   回落到用户名；连用户名都没有时返回空串，界面**显式写出"未知"**，
 *   而不是留一行空白（与积分显示「—」同一个原则：缺失要看得见）。
 *
 * ⚠️ 本函数**不做状态过滤**：一条 `revoked` 记录照样能解出模型来（它要被归类，
 *   不能被当成"解不出来"而混进"解析失败的丢弃"里）。归类由 `readShareLists()` 做。
 */
export function normalizeStudentShare(raw: unknown): StudentShare | null {
  if (raw === null || typeof raw !== 'object') return null;
  const record = raw as Record<string, unknown>;
  const str = (key: string): string =>
    typeof record[key] === 'string' ? (record[key] as string).trim() : '';

  const id = str('id');
  if (!id) return null;

  return {
    id,
    parentId: str('toParentId') || str('parentId'),
    parentNickname: str('toParentNickname') || str('parentNickname'),
    parentUsername: str('toParentUsername') || str('parentUsername'),
    permission: record.permission === 'viewer' ? 'viewer' : 'editor',
    status: readShareStatus(record.status),
    createdAt: str('createdAt'),
  };
}

/**
 * 把共享接口的响应拆成**两张列表**：仍然有效的「已共享的家长」与
 * 还没生效的「待对方同意的邀请」。
 *
 * ⚠️ 这个函数就是为了修"**已取消共享的人还留在「已共享的家长」里**"这个隐患
 *   （2026-10-14）：服务端 `listStudentShares()` 的 WHERE 原来只按 `studentId`
 *   过滤，`revoked`（owner 取消了）/ `left`（对方退出了）这些**已经失效**的
 *   关系仍然会列出来 —— 名单上的人看起来还有权限，其实早就没有了。
 *   状态口径只有一处：`@fht/shared` 的 `isInvisibleShareStatus()`。
 *
 * 向后兼容：优先读新响应的 `shares`（全部是 accepted）/ `pendingInvites`；
 * 老响应只有一个 `shares`（可能混着 pending）时，把 `pending` 挑到邀请那一组，
 * 失效的一律丢掉 —— 于是"已取消共享的人不会出现在已共享里"在**前端**也成立，
 * 不依赖服务端是否已经部署了新的查询。
 */
export function readShareLists(data: unknown): StudentShareLists {
  const record = pickObject(data, {}) as Record<string, unknown>;
  const hasSplitPayload = Array.isArray(record.pendingInvites);

  const parse = (raw: unknown): StudentShare | null => normalizeStudentShare(raw);

  if (hasSplitPayload) {
    // 服务端已经分好组（见 shares.service.ts）。这里仍然按状态过一遍：
    // 后端若哪天把 pending 或失效行混进 `shares`，界面也不会把它冒充成"已共享"。
    // 分类口径与 `@fht/shared` 的 `shareListBucketOf()` 一致。
    return {
      shares: pickArray<unknown>(record, 'shares')
        .map(parse)
        .filter((share): share is StudentShare => share !== null)
        .filter((share) => share.status === 'accepted'),
      pendingInvites: pickArray<unknown>(record, 'pendingInvites')
        .map(parse)
        .filter((share): share is StudentShare => share !== null)
        .filter((share) => share.status === 'pending'),
    };
  }

  const all = pickArray<unknown>(record, 'shares', 'items')
    .map(parse)
    .filter((share): share is StudentShare => share !== null);

  return {
    // 老响应：只有 accepted 能进「已共享」，revoked/left/rejected/expired 全部丢掉
    shares: all.filter((share) => share.status === 'accepted'),
    pendingInvites: all.filter((share) => share.status === 'pending'),
  };
}

/**
 * 某个学生的共享列表：孩子详情页的「已共享的家长」+「待对方同意的邀请」。
 *
 * 用 `pickArray` 而不是 `data.shares ?? []`：键名万一变了，
 * DEV 下会打出一条"候选键未命中"的告警（见 `shape.ts`），
 * 而不是安安静静地显示成"还没有其他家长可以看到这个孩子"。
 */
export async function listStudentShares(studentId: string): Promise<StudentShareLists> {
  return withFallback(
    async () => {
      const data = await api.get<unknown>(`/students/${studentId}/shares`);
      return readShareLists(data);
    },
    { shares: [], pendingInvites: [] } as StudentShareLists,
  );
}

// ---------------------------------------------------------------- 今日打卡
//
// 已迁移到 `lib/api/checkins.ts` 的 `studentToday()`（含计时字段、分组与降级三态）。
// 保留 `resolveUiMode` 在这里，是因为学生档案的年级字段由本模块负责读取。

/** 把"接口给的 uiMode"与"本地按年级推算的 uiMode"合并：家长覆盖 > 后端 > 年级。 */
export function resolveUiMode(student: {
  gradeCode: string;
  uiModeOverride: UiMode | null;
  serverUiMode?: UiMode;
}): UiMode {
  if (student.uiModeOverride) return student.uiModeOverride;
  if (student.serverUiMode) return student.serverUiMode;
  return gradeToUiMode(student.gradeCode);
}

// ---------------------------------------------------------------- 积分（只读余额）
//
// 完整的积分明细 / 手动调分 / 纠错冲正在 `lib/api/points.ts`。
// 这里只留一个余额查询：孩子详情页要显示的那个大数字。

/**
 * 某个学生的积分余额；**接口没就绪 / 失败时返回 `null`**（不是 `{ balance: 0 }`）。
 *
 * ⚠️ 这里原来是 `withFallback(..., { balance: 0, totalEarned: 0, totalSpent: 0 })`：
 *   `GET /students/:id/points` 只要返回 404/501/502/503，兜底就是 **余额 0**，
 *   于是孩子详情页的「当前积分」在接口坏掉时显示一个**看起来完全正常的 0 分**。
 *   这与本项目当天刚修过的"孩子卡片 0 积分""统计页恒为 0"是同一类病：
 *   **拿一个假值冒充真值**。
 *
 * 现在的形状：`PointsSummary | null`
 *   - 接口正常 → 返回响应（`balance` 真的是 `0` 就是 `0`，界面照写「0」）
 *   - 接口未就绪 / 失败 → `null`，界面显示「—」（缺失占位，见 `DURATION_PLACEHOLDER`
 *     同一约定），并且**加载中**另有加载态 —— 三种情形互不冒充。
 *
 * 注意：这里只兜 404/501/502/503（`tryRequest` 的语义）；401/403/网络错误照常抛出，
 * 由 React Query 记成 `isError`，界面同样显示「—」而不是 0。
 */
export async function studentPoints(studentId: string): Promise<PointsSummary | null> {
  const result = await tryRequest(() => api.get<unknown>(`/students/${studentId}/points`));
  if (result.state === 'notReady') return null;

  const record = pickObject(result.data, {}) as Record<string, unknown>;
  const balance = toOptionalNumber(record.balance);
  // 响应里没有可用的 `balance` → 视为"这次没说"，返回 null（界面「—」），
  // **绝不**用 0 顶替。真的 0 分是 `toOptionalNumber` 返回的数字 0，照原样给出去。
  if (balance === null) return null;

  const totalEarned = toOptionalNumber(record.totalEarned);
  const totalSpent = toOptionalNumber(record.totalSpent);
  return {
    balance,
    ...(totalEarned === null ? {} : { totalEarned }),
    ...(totalSpent === null ? {} : { totalSpent }),
  };
}
